import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

// Predisposizione COMPLETA della logica di accessibilità (vetrina pubblica).
// Le preferenze sono persistite in localStorage e applicate via classi/inline su <html>.
// Alla smontatura (uscita dalla vetrina) tutto viene ripristinato, così CRM/Area Clienti
// non vengono influenzati.

const STORAGE_KEY = 'sila_a11y_v1';

export const DEFAULT_A11Y = {
  fontScale: 1,          // 0.9 .. 1.4
  highContrast: false,
  reduceMotion: false,
  dyslexiaFont: false,
  underlineLinks: false,
  bigCursor: false,
  readingGuide: false,
};

const AccessibilityContext = createContext(null);

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_A11Y, ...JSON.parse(raw) };
  } catch (e) { /* noop */ }
  // NB: la preferenza di sistema "riduci movimento" è già rispettata via CSS
  // (@media prefers-reduced-motion). NON la attiviamo qui, altrimenti l'indicatore
  // "modificato" (pallino) apparirebbe senza che l'utente abbia toccato nulla.
  return { ...DEFAULT_A11Y };
}

// Confronto robusto (indipendente dall'ordine delle chiavi) con i valori di default.
function isDefaultSettings(s) {
  return Object.keys(DEFAULT_A11Y).every((k) => s[k] === DEFAULT_A11Y[k]);
}

function applyToDom(s) {
  const root = document.documentElement;
  root.classList.toggle('a11y-contrast', !!s.highContrast);
  root.classList.toggle('a11y-reduce-motion', !!s.reduceMotion);
  root.classList.toggle('a11y-dyslexia', !!s.dyslexiaFont);
  root.classList.toggle('a11y-underline', !!s.underlineLinks);
  root.classList.toggle('a11y-big-cursor', !!s.bigCursor);
  const scale = Number(s.fontScale || 1);
  root.style.fontSize = scale && scale !== 1 ? `${Math.round(16 * scale)}px` : '';
}

function clearDom() {
  const root = document.documentElement;
  ['a11y-contrast', 'a11y-reduce-motion', 'a11y-dyslexia', 'a11y-underline', 'a11y-big-cursor']
    .forEach((c) => root.classList.remove(c));
  root.style.fontSize = '';
}

export function AccessibilityProvider({ children }) {
  const [settings, setSettings] = useState(loadInitial);

  // Applica + persisti a ogni cambiamento
  useEffect(() => {
    applyToDom(settings);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch (e) { /* noop */ }
  }, [settings]);

  // Ripristina all'uscita dalla vetrina
  useEffect(() => () => clearDom(), []);

  // Guida di lettura: barra che segue il cursore
  useEffect(() => {
    if (!settings.readingGuide) return undefined;
    const bar = document.createElement('div');
    bar.setAttribute('data-a11y-guide', '');
    bar.style.cssText = 'position:fixed;left:0;right:0;height:44px;z-index:9998;pointer-events:none;background:hsla(var(--brand-sun)/0.18);border-top:2px solid hsl(var(--brand-sun));border-bottom:2px solid hsl(var(--brand-sun));transform:translateY(-50%);transition:top .05s linear;';
    document.body.appendChild(bar);
    const move = (e) => { bar.style.top = `${e.clientY}px`; };
    window.addEventListener('mousemove', move);
    return () => { window.removeEventListener('mousemove', move); bar.remove(); };
  }, [settings.readingGuide]);

  const value = useMemo(() => ({
    settings,
    set: (patch) => setSettings((s) => ({ ...s, ...patch })),
    toggle: (key) => setSettings((s) => ({ ...s, [key]: !s[key] })),
    incFont: () => setSettings((s) => ({ ...s, fontScale: Math.min(1.4, Math.round((s.fontScale + 0.1) * 10) / 10) })),
    decFont: () => setSettings((s) => ({ ...s, fontScale: Math.max(0.9, Math.round((s.fontScale - 0.1) * 10) / 10) })),
    reset: () => setSettings({ ...DEFAULT_A11Y }),
    isDefault: isDefaultSettings(settings),
  }), [settings]);

  return <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>;
}

export function useAccessibility() {
  return useContext(AccessibilityContext);
}
